import React, { useCallback, useContext, useEffect, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import { XAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, YAxis, BarChart, Bar, Legend } from "recharts";
import { useSelector } from "react-redux";

import { ThemeContext } from "../../context/Themes";
import { AnalyticsService } from "../../services";
import { Heading, Text, Icon } from "../elements";
import { formatAmountSuffix } from "../../utils/formatters";

const BalanceTooltip = ({ active, payload, label, colors }) => {
  if (!active || !payload?.length) return null;
  return (
    <Box
      sx={{
        background: colors.bg,
        borderRadius: "8px",
        padding: "11px 18px 12px",
        border: `1px solid ${colors.frame}`,
        boxShadow: "0px 12px 25px 0px rgb(0 0 0 / 12%)",
      }}
    >
      <Heading
        as="h6"
        style={{
          color: colors.title,
          marginBottom: "8px",
          textTransform: "uppercase",
          fontSize: "12px",
          fontWeight: "600",
        }}
      >
        {label}
      </Heading>
      {payload.map((entry, index) => (
        <Box key={index} display="flex" alignItems="center" gap={1} mb={0.5}>
          <Box sx={{ width: 12, height: 12, backgroundColor: entry.color, borderRadius: "2px" }} />
          <Text
            as="span"
            style={{
              color: colors.text,
              fontSize: "14px",
              fontWeight: "500",
              textTransform: "capitalize",
            }}
          >
            {entry.name}: {formatAmountSuffix(entry.value, true)}
          </Text>
        </Box>
      ))}
    </Box>
  );
};

export function BalanceSummaryChart() {
  const { theme } = useContext(ThemeContext);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const userDetails = useSelector((state) => state.user?.user);
  const companyId = userDetails?.company?.id;

  const dark = { border: "#39496b", frame: "#2f3f61", title: "#f0f0f0", text: "#d1d1d1", bg: "#1b2b4d" };
  const light = { border: "#d1d1d1", frame: "#f0f0f0", title: "#403e57", text: "#5e5d72", bg: "#ffffff" };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const { data: responseData } = await AnalyticsService.getCompanyBalanceSummary({
        ...(companyId ? { companyId } : {}),
      });
      setData(responseData);
    } catch (error) {
      console.error(`🚀 ~ fetchData ~ error:`, error);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Process data for Recharts BarChart
  const processChartData = () => {
    if (!data) return [];
    return [
      {
        name: "Clients",
        balance: parseFloat(data.clientsBalance || 0),
        advance: parseFloat(data.clientsAdvance || 0),
        icon: "people_outline",
      },
      {
        name: "Workers",
        balance: parseFloat(data.workersBalance || 0),
        advance: parseFloat(data.workersAdvance || 0),
        icon: "inventory_2",
      },
      //   {
      //     name: "Employees",
      //     balance: parseFloat(data.employeesBalance || 0),
      //     advance: parseFloat(data.employeesAdvance || 0),
      //     icon: "badge",
      //   },
      {
        name: "Vendors",
        balance: parseFloat(data.vendorsBalance || 0),
        advance: parseFloat(data.vendorsAdvance || 0),
        icon: "storefront",
      },
      {
        name: "Subcontractors",
        balance: parseFloat(data.subcontractorsBalance || 0),
        advance: parseFloat(data.subcontractorsAdvance || 0),
        icon: "engineering",
      },
    ];
  };

  const chartData = processChartData();

  return (
    <Box className="mc-card" sx={{ mt: 3 }}>
      <Box className="mc-card-header" style={{ display: "flex", justifyContent: "center" }}>
        <Heading as="h2" className="mc-card-title" style={{ fontSize: "20px", fontWeight: 600, paddingBottom: "10px" }}>
          Balance & Advance Summary
        </Heading>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress size={40} />
        </Box>
      ) : chartData.length > 0 ? (
        <Box>
          <Box
            sx={{
              display: "flex",
              gap: "24px",
              mb: 3,
              flexWrap: "wrap",
              justifyContent: "space-between",
              borderBottom: `1px solid ${theme !== "light_mode" ? dark.border : light.border}`,
              paddingBottom: "16px",
            }}
          >
            {chartData.map((item, index) => (
              <Box key={index} sx={{ display: "flex", flexDirection: "column", minWidth: "120px" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                  <Icon className="material-icons" style={{ color: "#2351af", fontSize: "16px" }}>
                    {item.icon}
                  </Icon>
                  <Text
                    as="p"
                    style={{ fontSize: "13px", fontWeight: "500", color: theme !== "light_mode" ? dark.text : light.text }}
                  >
                    {item.name}
                  </Text>
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                  <Heading as="h6" style={{ fontSize: "15px", color: theme !== "light_mode" ? dark.title : light.title }}>
                    <span style={{ fontSize: "12px", fontWeight: "normal", color: "#a0a0a0", marginRight: "4px" }}>Balance:</span>
                    {formatAmountSuffix(item.balance, true)}
                  </Heading>
                  <Heading as="h6" style={{ fontSize: "15px", color: theme !== "light_mode" ? dark.title : light.title }}>
                    <span style={{ fontSize: "12px", fontWeight: "normal", color: "#a0a0a0", marginRight: "4px" }}>Advance:</span>
                    {formatAmountSuffix(item.advance, true)}
                  </Heading>
                </Box>
              </Box>
            ))}
          </Box>

          <Box sx={{ height: 350, minHeight: 300, width: "100%" }}>
            {/* Bar Chart */}
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 20, right: 10, left: -20, bottom: 5 }}>
                <CartesianGrid
                  stroke={theme !== "light_mode" ? dark.border : light.border}
                  strokeDasharray="1 3"
                  vertical={false}
                />
                <XAxis
                  dataKey="name"
                  tickLine={false}
                  axisLine={false}
                  stroke={theme !== "light_mode" ? dark.text : light.text}
                  fontSize="12px"
                  fontWeight="500"
                  tickMargin={12}
                />
                <YAxis
                  tickLine={false}
                  axisLine={false}
                  stroke={theme !== "light_mode" ? dark.text : light.text}
                  fontSize="12px"
                  fontWeight="500"
                  tickFormatter={(value) => formatAmountSuffix(value)}
                  width={80}
                />
                <RechartsTooltip
                  content={<BalanceTooltip colors={theme !== "light_mode" ? dark : light} />}
                  cursor={{ fill: theme !== "light_mode" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)" }}
                />
                <Legend iconType="circle" wrapperStyle={{ paddingTop: "20px" }} />
                <defs>
                  <linearGradient id="balanceGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#2351af" stopOpacity={0.9} />
                    <stop offset="100%" stopColor="#2351af" stopOpacity={0.6} />
                  </linearGradient>
                  <linearGradient id="advanceGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#c71414" stopOpacity={0.9} />
                    <stop offset="100%" stopColor="#c71414" stopOpacity={0.6} />
                  </linearGradient>
                </defs>
                <Bar dataKey="balance" name="Total Balance" fill="url(#balanceGradient)" radius={[4, 4, 0, 0]} barSize={32} />
                <Bar dataKey="advance" name="Total Advance" fill="url(#advanceGradient)" radius={[4, 4, 0, 0]} barSize={32} />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Box>
      ) : (
        <Box sx={{ py: 6, textAlign: "center" }}>
          <Text>No balance data available</Text>
        </Box>
      )}
    </Box>
  );
}
